import { useState, useCallback, useEffect, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation, useSearch } from "wouter";
import { ChatInterface } from "@/components/ChatInterface";
import { MobilePreview } from "@/components/MobilePreview";
import { ImagePanel } from "@/components/ImagePanel";
import { Button } from "@/components/ui/button";
import { Code, Loader2 } from "lucide-react";
import { ChatMessage, LanguageCode, AwardPage, GenerateHtmlResponse, AnalyzedImage, GeneratedPage } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useCMS } from "@/hooks/use-cms";
import { useI18n } from "@/hooks/use-i18n";
import type { PresetId } from "@/components/PresetSelector";
import { captureEvent, identifyUser } from "@/lib/posthog";

interface PageData {
  page: GeneratedPage;
  html: string | null;
  messages: ChatMessage[];
  images: AnalyzedImage[];
}

export default function Home() {
  const search = useSearch();
  const [, setLocation] = useLocation();
  const searchParams = new URLSearchParams(search);
  const urlPageId = searchParams.get("pageId");
  
  const { targetLanguage, uiLanguage, contestId, editionId, setHtmlGetter, setDirty, isEmbedded, initialContent } = useCMS();
  const { t } = useI18n();
  
  const [pageId, setPageId] = useState<string | null>(urlPageId);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [html, setHtml] = useState("");
  const [currentLanguage, setCurrentLanguage] = useState<LanguageCode>(
    (targetLanguage as LanguageCode) || "en"
  );
  const [currentPage, setCurrentPage] = useState<AwardPage | null>(null);
  const [analyzedImages, setAnalyzedImages] = useState<AnalyzedImage[]>([]);
  const [isManuallyEdited, setIsManuallyEdited] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [selectedPresets, setSelectedPresets] = useState<PresetId[]>([]);
  const initStartedRef = useRef(false);
  const { toast } = useToast();
  
  const htmlRef = useRef(html);
  useEffect(() => {
    htmlRef.current = html;
  }, [html]);
  
  useEffect(() => {
    setHtmlGetter(() => htmlRef.current);
  }, [setHtmlGetter]);

  useEffect(() => {
    if (contestId && editionId) {
      identifyUser(`${contestId}:${editionId}`, {
        contestId,
        editionId,
      });
    }
  }, [contestId, editionId]);

  const hasContestParams = !!(contestId && editionId && targetLanguage);

  const pageQueryUrl = pageId
    ? contestId && editionId
      ? `/api/pages/${pageId}?contestId=${encodeURIComponent(contestId)}&editionId=${encodeURIComponent(editionId)}`
      : `/api/pages/${pageId}`
    : null;

  const { data: pageData, isLoading: isLoadingPage } = useQuery<PageData>({
    queryKey: ["/api/pages", pageId, contestId, editionId],
    queryFn: async () => {
      const res = await fetch(pageQueryUrl!);
      if (!res.ok) throw new Error("Failed to load page");
      return res.json();
    },
    enabled: !!pageId && !!pageQueryUrl,
  });

  const findOrCreateMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/pages/find-or-create", {
        contestId,
        editionId,
        currentLanguage: targetLanguage,
      });
      return response.json() as Promise<{ page: GeneratedPage; created: boolean }>;
    },
    onSuccess: ({ page }) => {
      setPageId(page.pageId);
      const params = new URLSearchParams();
      params.set("pageId", page.pageId);
      if (contestId) params.set("contestId", contestId);
      if (editionId) params.set("editionId", editionId);
      if (targetLanguage) params.set("targetLanguage", targetLanguage);
      setLocation(`/?${params.toString()}`, { replace: true });
      if (!page.htmlStoragePath) {
        setMessages([]);
        if (initialContent === null) {
          setHtml("");
        }
        setCurrentPage(null);
        setAnalyzedImages([]);
      }
      setCurrentLanguage((page.currentLanguage as LanguageCode) || "en");
    },
    onError: (error: Error) => {
      console.error("Failed to find or create page:", error);
      toast({
        title: t("errorTitle"),
        description: t("failedToInitialize"),
        variant: "destructive",
      });
    },
  });

  const createPageMutation = useMutation({
    mutationFn: async (data: { userId?: string; activyPageId?: string; contestId?: string; editionId?: string; currentLanguage?: string }) => {
      const response = await apiRequest("POST", "/api/pages", {
        ...data,
        contestId: contestId || undefined,
        editionId: editionId || undefined,
        currentLanguage: targetLanguage,
      });
      return response.json() as Promise<GeneratedPage>;
    },
    onSuccess: (page) => {
      setPageId(page.pageId);
      const params = new URLSearchParams();
      params.set("pageId", page.pageId);
      if (contestId) params.set("contestId", contestId);
      if (editionId) params.set("editionId", editionId);
      if (targetLanguage) params.set("targetLanguage", targetLanguage);
      setLocation(`/?${params.toString()}`, { replace: true });
      setMessages([]);
      if (initialContent === null) {
        setHtml("");
      }
      setCurrentPage(null);
      setAnalyzedImages([]);
      setCurrentLanguage((page.currentLanguage as LanguageCode) || "en");
      setIsInitialized(true);
    },
    onError: (error: Error) => {
      console.error("Failed to create page:", error);
      toast({
        title: t("errorTitle"),
        description: t("failedToInitialize"),
        variant: "destructive",
      });
    },
  });

  const saveMessageMutation = useMutation({
    mutationFn: async ({ pageId, message }: { pageId: string; message: ChatMessage }) => {
      await apiRequest("POST", `/api/pages/${pageId}/message`, { message });
    },
  });

  const saveHtmlMutation = useMutation({
    mutationFn: async ({ pageId, html, awardPage, currentLanguage }: { pageId: string; html: string; awardPage?: AwardPage; currentLanguage?: string }) => {
      await apiRequest("POST", `/api/pages/${pageId}/html`, { html, awardPage, currentLanguage });
    },
  });

  const saveImageMutation = useMutation({
    mutationFn: async ({ pageId, image, storagePath }: { pageId: string; image: AnalyzedImage; storagePath: string }) => {
      await apiRequest("POST", `/api/pages/${pageId}/image`, { image, storagePath });
    },
  });

  useEffect(() => {
    if (pageData && !isInitialized) {
      initStartedRef.current = true;
      setPageId(pageData.page.pageId);
      setMessages(pageData.messages);
      if (pageData.html && initialContent === null) {
        setHtml(pageData.html);
      }
      if (pageData.page.awardPageData) {
        setCurrentPage(pageData.page.awardPageData as AwardPage);
      }
      if (pageData.page.currentLanguage) {
        setCurrentLanguage(pageData.page.currentLanguage as LanguageCode);
      }
      setAnalyzedImages(pageData.images);
      setIsInitialized(true);
    }
  }, [pageData, isInitialized, initialContent]);

  useEffect(() => {
    if (initialContent !== null) {
      console.log(`[CMS] Applying initialContent (length: ${initialContent.length}, pageId: ${pageId})`);
      setHtml(initialContent);

      queryClient.invalidateQueries({ queryKey: ["/api/pages", pageId, contestId, editionId] });

      if (pageId && initialContent.length > 0) {
        console.log(`[CMS] Saving initialContent to DB for page: ${pageId}`);
        saveHtmlMutation.mutate({ pageId, html: initialContent });
      }
    }
  }, [initialContent]);

  useEffect(() => {
    if (initStartedRef.current) return;
    if (isInitialized) return;

    if (hasContestParams && !urlPageId) {
      initStartedRef.current = true;
      findOrCreateMutation.mutate();
      return;
    }

    if (!urlPageId) {
      initStartedRef.current = true;
      createPageMutation.mutate({});
      return;
    }

    if (urlPageId && !isLoadingPage && !pageData) {
      initStartedRef.current = true;
      createPageMutation.mutate({});
    }
  }, [urlPageId, isInitialized, hasContestParams, isLoadingPage, pageData]);

  const generateMutation = useMutation({
    mutationFn: async ({ prompt, presets }: { prompt: string; presets?: PresetId[] }) => {
      const uploadedImages = analyzedImages.map((img) => ({
        url: img.url,
        type: img.analysis?.suggestedType || "prize",
        labels: img.analysis?.labels || [],
        description: img.analysis?.description || "",
      }));
      const response = await apiRequest("POST", "/api/generate", {
        prompt,
        conversationHistory: messages,
        currentPage: currentPage || undefined,
        currentHtml: html || undefined,
        language: currentLanguage,
        uiLanguage: uiLanguage,
        uploadedImages,
        selectedPresets: presets,
      });
      return response.json() as Promise<GenerateHtmlResponse>;
    },
    onSuccess: (data, variables) => {
      const isFirstGeneration = !html;
      captureEvent("page_generated", {
        is_first_generation: isFirstGeneration,
        preset_count: variables.presets?.length ?? 0,
        selected_presets: variables.presets ?? [],
        html_length: data.html.length,
      });
      setHtml(data.html);
      setCurrentPage(data.awardPage);
      setIsManuallyEdited(false);
      addAssistantMessage(data.message);
      setDirty(true);
      
      if (pageId) {
        saveHtmlMutation.mutate({ pageId, html: data.html, awardPage: data.awardPage, currentLanguage });
      }
    },
    onError: (error: Error) => {
      console.error("Generation error:", error);
      addAssistantMessage(t("encounterError"));
      toast({
        title: t("generationFailed"),
        description: error.message || t("somethingWentWrong"),
        variant: "destructive",
      });
    },
  });

  const translateMutation = useMutation({
    mutationFn: async (targetLanguage: LanguageCode) => {
      if (!currentPage) throw new Error("No page to translate");
      const response = await apiRequest("POST", "/api/translate", {
        awardPage: currentPage,
        targetLanguage,
        currentHtml: html,
      });
      return response.json() as Promise<GenerateHtmlResponse>;
    },
    onSuccess: (data, translationTargetLanguage) => {
      setHtml(data.html);
      setCurrentPage(data.awardPage);
      setDirty(true);
      
      if (pageId) {
        saveHtmlMutation.mutate({ pageId, html: data.html, awardPage: data.awardPage, currentLanguage: translationTargetLanguage });
      }
    },
    onError: (error: Error) => {
      console.error("Translation error:", error);
      toast({
        title: t("translationFailed"),
        description: error.message || t("somethingWentWrong"),
        variant: "destructive",
      });
    },
  });

  const generateImageMutation = useMutation({
    mutationFn: async ({ prompt, type, pageId: currentPageId }: { prompt: string; type: "hero" | "prize" | "trophy"; pageId?: string | null }) => {
      const response = await apiRequest("POST", "/api/generate-image", {
        prompt,
        type,
        pageId: currentPageId,
      });
      const data = await response.json() as { imageUrl: string; imageId: string; storagePath: string };
      return data.imageUrl;
    },
    onError: (error: Error) => {
      console.error("Image generation error:", error);
      throw error;
    },
  });

  const addUserMessage = (content: string) => {
    const message: ChatMessage = {
      id: Date.now().toString(),
      role: "user",
      content,
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, message]);
    
    if (pageId) {
      saveMessageMutation.mutate({ pageId, message });
    }
  };

  const addAssistantMessage = (content: string) => {
    const message: ChatMessage = {
      id: (Date.now() + 1).toString(),
      role: "assistant",
      content,
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, message]);
    
    if (pageId) {
      saveMessageMutation.mutate({ pageId, message });
    }
  };

  const handleSendMessage = useCallback(
    (message: string, presets?: PresetId[]) => {
      addUserMessage(message);
      generateMutation.mutate({ prompt: message, presets });
    },
    [generateMutation, messages, pageId]
  );

  const handleTogglePreset = useCallback((id: PresetId) => {
    setSelectedPresets((prev) => {
      if (prev.includes(id)) {
        return prev.filter((p) => p !== id);
      }
      const prizePresets: PresetId[] = ["edition_end", "challenge_end", "guaranteed_prizes", "lottery"];
      if (id === "no_prizes") {
        return prev.filter((p) => !prizePresets.includes(p)).concat(id);
      }
      if (prizePresets.includes(id)) {
        return prev.filter((p) => p !== "no_prizes").concat(id);
      }
      return [...prev, id];
    });
  }, []);

  const handleLanguageChange = useCallback(
    (language: LanguageCode) => {
      setCurrentLanguage(language);
      if (currentPage) {
        translateMutation.mutate(language);
      }
    },
    [currentPage, translateMutation]
  );

  const handleImagesChange = useCallback((newImages: AnalyzedImage[]) => {
    const addedImages = newImages.filter(
      (newImg) => !analyzedImages.some((existingImg) => existingImg.id === newImg.id)
    );
    
    setAnalyzedImages(newImages);
    
    if (pageId) {
      addedImages.forEach((image) => {
        if (image.url && image.uploadStatus === "uploaded") {
          saveImageMutation.mutate({ pageId, image, storagePath: image.url });
        }
      });
    }
  }, [analyzedImages, pageId, saveImageMutation]);

  const handleHtmlChange = useCallback((updatedHtml: string) => {
    setHtml(updatedHtml);
    setIsManuallyEdited(true);
    setDirty(true);
    
    if (pageId) {
      saveHtmlMutation.mutate({ pageId, html: updatedHtml });
    }
  }, [pageId, saveHtmlMutation, setDirty]);

  const handleGenerateImage = useCallback(
    async (prompt: string, type: "hero" | "prize" | "trophy") => {
      const imageUrl = await generateImageMutation.mutateAsync({ prompt, type, pageId });
      return imageUrl;
    },
    [generateImageMutation, pageId]
  );

  const handleAddImagesToPage = useCallback(() => {
    if (!currentPage || analyzedImages.length === 0) return;
    
    const imageDescriptions = analyzedImages
      .filter(img => img.analysis)
      .map(img => `Image URL: ${img.url} - ${img.analysis?.description} (${img.analysis?.labels.slice(0, 3).join(", ")})`)
      .join("\n");
    
    const prompt = `Update the current awards page to include the uploaded prize images while preserving all existing content and styling.

Current HTML (preserve all manual edits):
${html}

Images to add:
${imageDescriptions}

Match each image to the appropriate award category based on its content and place it prominently in that section.
Keep all existing content, styling, and text exactly as it is - only add the images in appropriate places.`;
    
    addUserMessage("Add uploaded images to the page");
    generateMutation.mutate({ prompt });
  }, [currentPage, analyzedImages, generateMutation, html, pageId]);

  const isLoading = generateMutation.isPending;
  const isTranslating = translateMutation.isPending;
  const isGeneratingImage = generateImageMutation.isPending;
  const isPageLoading = isLoadingPage || findOrCreateMutation.isPending || createPageMutation.isPending || !isInitialized;

  if (isPageLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-muted-foreground">{t("loadingPage")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-background">
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        <div className="lg:w-[40%] min-h-0 flex-1 lg:flex-none lg:h-full border-b lg:border-b-0 lg:border-r flex flex-col overflow-hidden">
          <ImagePanel
            images={analyzedImages}
            onImagesChange={handleImagesChange}
            onGenerateImage={handleGenerateImage}
            onAddToPage={handleAddImagesToPage}
            isGenerating={isGeneratingImage}
            isAddingToPage={isLoading}
            hasExistingPage={!!currentPage}
          />
          <ChatInterface
            messages={messages}
            onSendMessage={handleSendMessage}
            isLoading={isLoading}
            selectedPresets={selectedPresets}
            onTogglePreset={handleTogglePreset}
          />
        </div>

        <div className="lg:w-[60%] min-h-0 flex-1 lg:flex-none lg:h-full flex flex-col overflow-hidden">
          <MobilePreview
            html={html}
            isLoading={isLoading || isTranslating}
            onRefresh={currentPage ? () => {
              captureEvent("page_regenerated", {
                selected_presets: selectedPresets,
                preset_count: selectedPresets.length,
              });
              generateMutation.mutate({ prompt: t("regeneratePage"), presets: selectedPresets.length > 0 ? selectedPresets : undefined });
            } : undefined}
            onHtmlChange={handleHtmlChange}
          />
        </div>
      </div>

      {html && (
        <div className="absolute bottom-4 right-4 lg:hidden">
          <Button
            size="sm"
            variant="outline"
            className="shadow-lg bg-background"
            onClick={async () => {
              let success = false;
              if (navigator.clipboard && window.isSecureContext) {
                try {
                  await navigator.clipboard.writeText(html);
                  success = true;
                } catch {}
              }
              if (!success) {
                const textarea = document.createElement("textarea");
                textarea.value = html;
                textarea.style.position = "fixed";
                textarea.style.left = "-9999px";
                textarea.style.top = "-9999px";
                document.body.appendChild(textarea);
                textarea.focus();
                textarea.select();
                try { success = document.execCommand("copy"); } catch {}
                document.body.removeChild(textarea);
              }
              if (success) {
                toast({
                  title: t("copiedTitle"),
                  description: t("copiedDescription"),
                });
              } else {
                toast({
                  title: t("failedToCopy"),
                  description: t("tryAgainOrDownload"),
                  variant: "destructive",
                });
              }
            }}
            data-testid="button-copy-mobile"
          >
            <Code className="w-4 h-4 mr-2" />
            {t("copyHTML")}
          </Button>
        </div>
      )}
    </div>
  );
}
